import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CapacitorHttp } from '@capacitor/core';
import {
    DocumentCategory,
    DocumentSearchRequest,
    DownloadDocument,
    Environment,
    ENVIRONMENT,
} from '@ld.consumer-portal/models';
import { AuthService, LoggingService, NativeDocumentService, PlatformService } from '@ld.consumer-portal/utils';
import { firstValueFrom } from 'rxjs';

const CATEGORY_TO_API_VALUE: Record<DocumentCategory, string> = {
  All: 'All',
  'Monthly Billing Statement': 'Billing Statements',
  Escrow: 'Escrow Statements',
  Letters: 'Letters, Default Letter',
  'Tax Documents': 'IRS 1099 INT,IRS 1098,IRS 1099',
};

@Injectable({
  providedIn: 'root',
})
export class DocumentsService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject<Environment>(ENVIRONMENT);
  private readonly authService = inject(AuthService);
  private readonly nativeDocumentService = inject(NativeDocumentService);
  private readonly platformService = inject(PlatformService);
  private readonly loggingService = inject(LoggingService);

  private readonly defaultMonthRange = this.environment.appSettings?.SEDMMonthRange ?? 13;

  async getDocuments(
    loanNumber: string,
    startDate?: string,
    endDate?: string,
    documentCategory: DocumentCategory = 'All'
  ): Promise<DownloadDocument[]> {
    const apiUrl = this.environment.appSettings?.SEDMDocumentList;

    if (!apiUrl) {
      throw new Error('SEDM Document List API URL not configured');
    }

    const now = new Date();
    const defaultEndDate = this.formatDateForApi(now);
    const defaultStartDate = this.formatDateForApi(
      new Date(now.getFullYear(), now.getMonth() - this.defaultMonthRange, now.getDate())
    );

    const apiCategory = CATEGORY_TO_API_VALUE[documentCategory] ?? CATEGORY_TO_API_VALUE['All'];

    const request: DocumentSearchRequest = {
      loanNumber,
      startDate: startDate ?? defaultStartDate,
      endDate: endDate ?? defaultEndDate,
      documentCategory: apiCategory,
    };

    const headers = this.buildHeaders();

    try {
      const response = await firstValueFrom(
        this.http.post<DownloadDocument[]>(`${apiUrl}/api/DocumentList`, request, { headers })
      );
      // Handle cases where response is not an array (e.g., 204 No Content returns string/null)
      if (!Array.isArray(response)) {
        return [];
      }
      return response.filter(
        (doc) => doc.documentDate && doc.documentType && doc.documentId
      );
    } catch (error: unknown) {
      // Treat 404 or 204 as "no documents found" rather than an error
      if (error instanceof HttpErrorResponse && (error.status === 404 || error.status === 204)) {
        return [];
      }
      this.loggingService.logException(error, `Error fetching documents for loan ${loanNumber}`);
      const message = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to fetch documents: ${message}`);
    }
  }

  async downloadDocument(document: DownloadDocument): Promise<Blob> {
    const apiUrl = this.environment.appSettings?.SEDMDownloadDocument;

    if (!apiUrl) {
      throw new Error('SEDM Document Download API URL not configured');
    }

    if (this.platformService.isNativeMobile()) {
      return this.downloadDocumentNative(document, apiUrl);
    }

    const headers = this.buildHeaders();

    try {
      return await firstValueFrom(
        this.http.post(`${apiUrl}/api/DocumentDownload`, document, {
          headers,
          responseType: 'blob',
        })
      );
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error downloading document ${document.documentType}`);
      const message = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to download document: ${message}`);
    }
  }

  private async downloadDocumentNative(document: DownloadDocument, apiUrl: string): Promise<Blob> {
    const token = this.authService.getToken();
    const userId = this.authService.getUserId() ?? '';
    const channelId = this.environment.appSettings?.channelId ?? '2';
    const subscriptionKey = this.environment.appSettings?.ocpApimSubscriptionSEDM ?? '';
    const url = `${apiUrl}/api/DocumentDownload`;

    try {
      const response = await CapacitorHttp.post({
        url,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf',
          'channel-id': channelId,
          'global-user-id': userId,
          'ocp-apim-subscription-key': subscriptionKey,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        data: document,
        responseType: 'blob',
      });

      if (response.status !== 200) {
        throw new Error(`HTTP ${response.status}`);
      }

      const base64Data = response.data as string;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: 'application/pdf' });
    } catch (error: unknown) {
      this.loggingService.logException(error, `Error downloading document (native) ${document.documentType}`);
      const message = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to download document: ${message}`);
    }
  }

  async downloadDocumentToBrowser(document: DownloadDocument): Promise<void> {
    const filename = `${document.loanNumber}_${document.documentType}_${document.documentDate}.pdf`;

    try {
      const blob = await this.downloadDocument(document);
      await this.nativeDocumentService.saveDocument(blob, filename);
    } catch (err) {
      this.loggingService.logException(err, 'Failed to download document:');
    }
  }

  async viewDocumentInBrowser(document: DownloadDocument): Promise<void> {
    const filename = `${document.loanNumber}_${document.documentType}_${document.documentDate}.pdf`;

    try {
      const blob = await this.downloadDocument(document);
      await this.nativeDocumentService.openDocument(blob, filename);
    } catch (err) {
      this.loggingService.logException(err, 'Failed to view document:');
      await this.downloadDocumentToBrowser(document);
    }
  }

  async getDocumentViewUrl(document: DownloadDocument): Promise<string> {
    try {
      const blob = await this.downloadDocument(document);
      return URL.createObjectURL(blob);
    } catch (error: unknown) {
      this.loggingService.logException(error, 'Failed to get document view URL');
      const message = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to get document URL: ${message}`);
    }
  }

  formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  formatDateForDisplay(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  parseDisplayDateToApi(displayDate: string): string {
    const [month, day, year] = displayDate.split('/');
    return `${year}${month}${day}`;
  }

  getDefaultStartDate(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - this.defaultMonthRange, now.getDate());
  }

  getDefaultEndDate(): Date {
    return new Date();
  }

  private buildHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
  }
}
